import { useCallback } from 'react';
import Dropzone from 'react-dropzone';
import AddDocumentDropzoneIcon from 'assets/icons/add-document-dropzone-icon.svg?react';
import { Grid, Link, Typography } from '@mui/material';

const DEFAULT_MAX_SIZE = 50 * 1024 * 1024;

export type CommonDropzoneProps = {
  setItem: (item: File) => void;
  maxSize?: number;
  maxSizeErrorMessage?: string;
};

const CommonDropzone = ({
  setItem,
  maxSize = DEFAULT_MAX_SIZE,
  maxSizeErrorMessage = `File size exceeds ${DEFAULT_MAX_SIZE} limit`,
}: CommonDropzoneProps) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles[0]?.size > maxSize) {
        return;
      }
      setItem(acceptedFiles[0]);
    },
    [setItem, maxSize],
  );

  return (
    <Dropzone
      data-testid="common-dropzone"
      onDrop={onDrop}
      accept={{ 'application/pdf': ['.pdf'] }}
      maxSize={maxSize}
      onDropRejected={(rejectedFiles) => {
        if (rejectedFiles[0]?.errors[0]?.code === 'file-too-large') {
          return;
        }
      }}
    >
      {({ getRootProps, getInputProps, isDragReject }) => (
        <Grid
          container
          {...getRootProps()}
          sx={{
            height: '80px',
            alignItems: 'center',
            backgroundColor: '#E5F2F6',
            justifyContent: 'center',
            display: 'flex',
            p: 0,
            border: isDragReject ? '2px dashed red' : 'none',
          }}
        >
          <Grid data-testid="common-dropzone-grid" item xs={3} sx={{ justifyContent: 'flex-end', display: 'flex' }}>
            <input data-testid="common-dropzone-file-input" type="file" {...getInputProps()} />
            <AddDocumentDropzoneIcon data-testid="common-dropzone-file-input-icon" style={{ marginRight: '1em' }} />
          </Grid>
          <Grid item xs={9} sx={{ display: 'flex', flexDirection: 'row' }}>
            Drag document here or
            <Link data-testid="common-dropzone-file-browse" sx={{ ml: 1, cursor: 'pointer' }}>
              browse
            </Link>
          </Grid>
          {isDragReject && (
            <Typography
              color="error"
              sx={{ position: 'absolute', bottom: -25, width: '100%', textAlign: 'center' }}
              data-testid="file-size-error"
            >
              {maxSizeErrorMessage}
            </Typography>
          )}
        </Grid>
      )}
    </Dropzone>
  );
};

export default CommonDropzone;
